<template>
  <div class="entity-header-wrapper">
    <!-- Barra superior de título y botones principales -->
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
      <div class="d-flex align-items-center flex-wrap">
        <h2 class="mb-0 mr-3">{{ title }}</h2>
        <slot name="title-extra"></slot>
      </div>

      <div class="d-flex align-items-center flex-wrap mt-2 mt-sm-0">
        <slot name="actions-left"></slot>

        <!-- Botón Desplegar Filtros -->
        <button
          v-if="showFilters"
          class="btn btn-sm btn-principal mr-2"
          :class="{ collapsed: !isOpen }"
          data-toggle="collapse"
          :href="'#' + filterId"
          role="button"
          :aria-expanded="isOpen ? 'true' : 'false'"
          :aria-controls="filterId"
          @click="onToggleFilters"
        >
          <i class="bi-filter"></i> Filtros
        </button>

        <!-- Botón Exportar / Descargar Excel -->
        <button
          v-if="showExport"
          class="btn btn-sm btn-principal mr-2"
          @click="$emit('export')"
          :title="exportTooltip || 'Descargar listado completo'"
        >
          <i class="bi-download"></i> {{ exportLabel }}
        </button>

        <!-- Botón Nuevo Registro -->
        <button
          v-if="showNew"
          class="btn btn-sm btn-principal"
          data-toggle="tooltip"
          data-placement="top"
          :title="newTooltip || ('Nuevo ' + title)"
          @click="$emit('new')"
        >
          <i class="bi-plus"></i> {{ newLabel || ('Nuevo ' + title) }}
        </button>

        <slot name="actions"></slot>
      </div>
    </div>

    <!-- Caja colapsable de filtros de búsqueda -->
    <div
      v-if="showFilters"
      class="collapse mb-3"
      :class="{ show: isOpen }"
      :id="filterId"
    >
      <div class="card card-body bg-light border-0 shadow-sm py-3 px-4">
        <div class="form-row align-items-end">
          <!-- Inputs específicos del módulo -->
          <slot name="filters"></slot>

          <!-- Filtro de Estado integrado opcional -->
          <div v-if="showStatusFilter" class="form-group col-md-3 mb-2">
            <label class="font-weight-bold small text-muted mb-1" :for="filterId + '_status'">Por Estado</label>
            <select
              class="form-control"
              :id="filterId + '_status'"
              :value="statusValue"
              @change="onStatusChange($event.target.value)"
            >
              <option :value="null">Todos los estados</option>
              <option :value="1">Activo</option>
              <option :value="0">Inactivo</option>
            </select>
          </div>

          <!-- Botón de Búsqueda -->
          <div v-if="showSearchButton" class="form-group col-auto mb-2">
            <button
              type="button"
              @click="$emit('search')"
              class="btn btn-info px-4"
            >
              <i class="bi-search"></i> {{ searchButtonText }}
            </button>
            <slot name="filter-actions"></slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "EntityHeader",

  props: {
    title: {
      type: String,
      required: true,
    },
    filterId: {
      type: String,
      default: "filtroscollapse",
    },
    showFilters: {
      type: Boolean,
      default: true,
    },
    showExport: {
      type: Boolean,
      default: true,
    },
    exportLabel: {
      type: String,
      default: "Descargar listado completo",
    },
    exportTooltip: {
      type: String,
      default: "Descargar listado completo",
    },
    showNew: {
      type: Boolean,
      default: true,
    },
    newLabel: {
      type: String,
      default: "",
    },
    newTooltip: {
      type: String,
      default: "",
    },
    showSearchButton: {
      type: Boolean,
      default: true,
    },
    searchButtonText: {
      type: String,
      default: "Buscar",
    },
    showStatusFilter: {
      type: Boolean,
      default: false,
    },
    status: {
      type: [Number, String],
      default: null,
    },
    defaultOpen: {
      type: Boolean,
      default: null,
    },
    hasActiveFilters: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isOpen: this.determineInitialOpen(),
    };
  },

  computed: {
    statusValue() {
      if (this.status === null || this.status === undefined || this.status === "null" || this.status === "") {
        return null;
      }
      return Number(this.status);
    },
  },

  watch: {
    status() {
      if (this.showStatusFilter && this.statusValue !== null && !this.isOpen) {
        this.openCollapse();
      }
    },
    hasActiveFilters(newVal) {
      if (newVal && !this.isOpen) {
        this.openCollapse();
      }
    },
    defaultOpen(newVal) {
      if (newVal && !this.isOpen) {
        this.openCollapse();
      } else if (newVal === false && this.isOpen) {
        this.closeCollapse();
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      const el = document.getElementById(this.filterId);
      if (el && window.$) {
        window.$(el).off("show.bs.collapse hide.bs.collapse");
        window.$(el).on("show.bs.collapse", () => {
          this.isOpen = true;
        });
        window.$(el).on("hide.bs.collapse", () => {
          this.isOpen = false;
        });
      }
    });
  },

  beforeDestroy() {
    const el = document.getElementById(this.filterId);
    if (el && window.$) {
      window.$(el).off("show.bs.collapse hide.bs.collapse");
    }
  },

  methods: {
    determineInitialOpen() {
      if (this.defaultOpen !== null && this.defaultOpen !== undefined) {
        return Boolean(this.defaultOpen);
      }
      if (this.hasActiveFilters) {
        return true;
      }
      if (this.showStatusFilter && this.statusValue !== null) {
        return true;
      }
      return false;
    },

    onToggleFilters() {
      this.$emit("toggle-filters");
      if (!window.$) {
        this.isOpen = !this.isOpen;
      }
    },

    openCollapse() {
      this.isOpen = true;
      if (window.$) {
        window.$("#" + this.filterId).collapse("show");
      }
    },

    closeCollapse() {
      this.isOpen = false;
      if (window.$) {
        window.$("#" + this.filterId).collapse("hide");
      }
    },

    onStatusChange(val) {
      const parsed = (val === "null" || val === "" || val === null) ? null : Number(val);
      this.$emit("update:status", parsed);
      this.$emit("status-change", parsed);
    },
  },
};
</script>

<style scoped>
</style>

